import { useState } from 'react';

interface HorarioDia {
  inicio: string;
  fin: string;
  activo: boolean;
}

interface HorariosConfig {
  [key: string]: HorarioDia;
}

interface Props {
  horariosIniciales: HorariosConfig;
  onSave: (horarios: HorariosConfig) => void;
  onClose: () => void;
}

const diasSemana = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

export default function ConfigurarHorariosModal({ horariosIniciales, onSave, onClose }: Props) {
  const [horarios, setHorarios] = useState<HorariosConfig>(() => {
    const copy: HorariosConfig = {};
    for (const key of Object.keys(horariosIniciales)) {
      copy[key] = { ...horariosIniciales[key] };
    }
    return copy;
  });
  const [saved, setSaved] = useState(false);

  const handleToggle = (dia: string) => {
    setHorarios((prev) => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        activo: !prev[dia].activo,
        inicio: !prev[dia].activo ? '06:00' : prev[dia].inicio,
        fin: !prev[dia].activo ? '22:00' : prev[dia].fin,
      },
    }));
  };

  const handleTimeChange = (dia: string, field: 'inicio' | 'fin', value: string) => {
    setHorarios((prev) => ({
      ...prev,
      [dia]: { ...prev[dia], [field]: value },
    }));
  };

  const handleSave = () => {
    // Validar que inicio < fin en días activos
    for (const dia of diasSemana) {
      const h = horarios[dia.key];
      if (h.activo && h.inicio >= h.fin) {
        return;
      }
    }

    onSave(horarios);
    setSaved(true);
    setTimeout(() => {
      onClose();
    }, 800);
  };

  const aplicarHorarioSemana = () => {
    const lunesH = horarios['lunes'];
    setHorarios((prev) => {
      const updated = { ...prev };
      for (const dia of ['martes', 'miercoles', 'jueves', 'viernes']) {
        updated[dia] = { ...lunesH };
      }
      return updated;
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-auto">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Configurar Horarios</h3>
                <p className="text-sm text-gray-500 mt-1">Define los horarios de apertura y cierre para cada día</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
          </div>

          <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5 p-3 bg-teal-50 rounded-lg border border-teal-100">
              <div className="flex items-center gap-2">
                <i className="ri-magic-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                <span className="text-sm text-teal-700 font-medium">Copiar horario de Lunes a toda la semana (Lun-Vie)</span>
              </div>
              <button
                onClick={aplicarHorarioSemana}
                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Aplicar
              </button>
            </div>

            <div className="space-y-3">
              {diasSemana.map((dia) => {
                const h = horarios[dia.key];
                const errorHora = h.activo && h.inicio >= h.fin;
                return (
                  <div
                    key={dia.key}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                      h.activo ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => handleToggle(dia.key)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                        h.activo ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          h.activo ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      ></span>
                    </button>

                    <span className={`w-24 text-sm font-medium ${h.activo ? 'text-gray-800' : 'text-gray-400'}`}>{dia.label}</span>

                    {h.activo ? (
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Apertura</label>
                          <input
                            type="time"
                            value={h.inicio}
                            onChange={(e) => handleTimeChange(dia.key, 'inicio', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                        <span className="text-gray-400">—</span>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Cierre</label>
                          <input
                            type="time"
                            value={h.fin}
                            onChange={(e) => handleTimeChange(dia.key, 'fin', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                        {errorHora && (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>
                            Hora inválida
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Cerrado</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saved}
              className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                saved ? 'bg-green-500' : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {saved ? (
                <>
                  <i className="ri-check-line"></i>
                  Guardado
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Guardar Horarios
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
